import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import ModalShell from '../ui/ModalShell';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../context/AuthContext';
import { buildSelectStyles } from './ModalCotizacionVolumen';
import { generarPropuestaAlquiler, generarContratoAlquiler } from '../../utils/pdf/propuestaAlquiler';
import CamposContrato, { GUARDADO_LOCADOR, LOCADOR_INICIAL } from './CamposContrato';
import ModalActaEntrega from './ModalActaEntrega';

// Propuesta de alquiler de equipos a empresas (31-oct-2026). Los montos se cargan SIN IVA, como se negocian;
// el PDF suma el 21%. El cliente es opcional: la propuesta puede salir antes de darlo de alta.
// Los últimos valores quedan guardados en este navegador para la próxima propuesta.
// 31-oct-2026: tres documentos con los mismos datos: cotización corta (para quien pide precio, por defecto),
// propuesta completa y contrato (cuando aceptan; pide quién firma por cada parte).
const GUARDADO = 'propuesta_alquiler_ultima';
const INICIAL = {
    cantidad: '5', equipo: 'Dispenser de agua frío/calor', abonoNeto: '32500', instalacionNeto: '32500', bonificadas: '3',
    contratoMeses: '12', ajusteMeses: '6', diaPagoHasta: '10', validezDias: '15', observaciones: '', condicionIva: 'RESPONSABLE_INSCRIPTO',
};
const DOCS = [['corta', 'Cotización'], ['completa', 'Completa'], ['contrato', 'Contrato']];
const leer = (k, def) => { try { return { ...def, ...JSON.parse(localStorage.getItem(k) || '{}') }; } catch { return def; } };
const CONDICIONES = [['RESPONSABLE_INSCRIPTO', 'Responsable Inscripto'], ['MONOTRIBUTO', 'Monotributo'], ['EXENTO', 'IVA Exento'], ['CONSUMIDOR_FINAL', 'Consumidor final']];

const INPUT = 'w-full h-11 px-3 rounded-xl outline-none bg-chip border border-black/[0.07] dark:border-white/[0.07] text-ink text-sm font-bold focus:border-brand-red';
const LABEL = 'block text-label font-black text-muted uppercase mb-1.5 tracking-widest';
const n = (v) => Number(String(v).replace(/\./g, '').replace(',', '.')) || 0;
const pesos = (v) => `$ ${Math.round(v).toLocaleString('es-AR')}`;

function Campo({ label, children, className = '' }) {
    return <label className={`block ${className}`}><span className={LABEL}>{label}</span>{children}</label>;
}

export default function ModalPropuestaAlquiler({ onCerrar }) {
    const { isDark } = useTheme();
    const { usuario } = useAuth();
    const [clientes, setClientes] = useState([]);
    const [clienteOpt, setClienteOpt] = useState(null);
    const [sedeId, setSedeId] = useState('');
    const [acta, setActa] = useState(false);
    const [tipoDoc, setTipoDoc] = useState('corta');
    const [locador, setLocador] = useState(() => leer(GUARDADO_LOCADOR, LOCADOR_INICIAL));
    const [representante, setRepresentante] = useState({ nombre: '', dni: '', cargo: '' });
    const [preaviso, setPreaviso] = useState('30');
    const [f, setF] = useState(() => {
        try { return { ...INICIAL, ...JSON.parse(localStorage.getItem(GUARDADO) || '{}'), observaciones: '' }; } catch { return INICIAL; }
    });
    const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));

    useEffect(() => {
        api.get('/clientes?page=0&size=500')
            .then(r => setClientes((r.data.content || r.data).map(c => ({ value: c.id, label: c.nombre, c }))))
            .catch(() => toast.error('No se pudieron cargar los clientes'));
    }, []);

    const cliente = clienteOpt?.c || null;
    const sedes = (cliente?.sedes || []).filter(s => s.direccion);
    const sede = sedes.find(s => String(s.id) === String(sedeId)) || sedes[0] || null;

    const resumen = useMemo(() => {
        const cant = Math.max(1, n(f.cantidad));
        const bonif = Math.min(cant, n(f.bonificadas));
        return { mensual: n(f.abonoNeto) * cant * 1.21, inicial: n(f.instalacionNeto) * (cant - bonif) * 1.21 };
    }, [f]);

    const generar = () => {
        if (n(f.cantidad) < 1 || n(f.abonoNeto) <= 0) { toast.error('Completá la cantidad y el abono por equipo'); return; }
        try { localStorage.setItem(GUARDADO, JSON.stringify({ ...f, observaciones: '' })); } catch { /* sin guardado */ }
        const datosA = {
            cantidad: n(f.cantidad), equipo: f.equipo.trim(), abonoNeto: n(f.abonoNeto), instalacionNeto: n(f.instalacionNeto),
            bonificadas: n(f.bonificadas), contratoMeses: n(f.contratoMeses) || 12, ajusteMeses: n(f.ajusteMeses) || 6,
            diaPagoHasta: n(f.diaPagoHasta) || 10,
        };
        if (tipoDoc === 'contrato') {
            if (!cliente) { toast.error('Para el contrato elegí la empresa de Clientes'); return; }
            if (!locador.nombre.trim() || !locador.cuit.trim()) { toast.error('Falta el titular y el CUIT del locador'); return; }
            try { localStorage.setItem(GUARDADO_LOCADOR, JSON.stringify(locador)); } catch { /* sin guardado */ }
            try {
                generarContratoAlquiler({
                    autor: usuario?.nombre,
                    c: {
                        ...datosA, preavisoDias: n(preaviso) || 30, locador, representante,
                        locatario: { nombre: cliente.nombre, cuit: cliente.cuilDni, domicilio: cliente.direccion || sede?.direccion },
                        domicilioInstalacion: sede?.direccion || cliente.direccion,
                    },
                });
            } catch (e) { console.error(e); toast.error('No se pudo armar el PDF'); }
            return;
        }
        try {
            generarPropuestaAlquiler({
                cliente: cliente ? { ...cliente, condicionIva: cliente.condicionIva } : { condicionIva: f.condicionIva },
                sede, autor: usuario?.nombre,
                a: { ...datosA, indice: 'IPC (INDEC)', validezDias: n(f.validezDias) || 15, observaciones: f.observaciones, resumido: tipoDoc === 'corta' },
            });
        } catch (e) {
            console.error(e);
            toast.error('No se pudo armar el PDF');
        }
    };

    return (
        <ModalShell titulo="Alquiler de equipos" subtitulo="Montos sin IVA · el PDF suma el 21%" onCerrar={onCerrar}
            pie={
                <div className="flex gap-2">
                    {/* Cuando la empresa acepta y se instalan los equipos: el acta con la firma de quien recibe */}
                    <button type="button" onClick={() => setActa(true)} className="flex-1 h-12 rounded-xl font-black text-body text-ink bg-chip active:scale-95">
                        Acta de entrega
                    </button>
                    <button type="button" onClick={generar} className="flex-[1.4] h-12 rounded-xl font-black text-body text-white bg-brand-red active:scale-95">
                        {tipoDoc === 'contrato' ? 'Descargar contrato' : 'Descargar PDF'}
                    </button>
                </div>
            }>
            <div className="space-y-3">
                <div className="grid grid-cols-3 gap-1 p-1 rounded-xl bg-chip" role="tablist" aria-label="Documento">
                    {DOCS.map(([v, t]) => (
                        <button key={v} type="button" role="tab" aria-selected={tipoDoc === v} onClick={() => setTipoDoc(v)}
                            className={`h-9 rounded-lg text-sm font-black transition-all ${tipoDoc === v ? 'bg-card text-ink shadow-sm' : 'text-muted'}`}>{t}</button>
                    ))}
                </div>
                <Campo label={tipoDoc === 'contrato' ? 'Empresa' : 'Empresa (opcional)'}>
                    <Select options={clientes} value={clienteOpt} onChange={(o) => { setClienteOpt(o); setSedeId(''); }} isClearable
                        placeholder="Elegir de Clientes…" noOptionsMessage={() => 'Sin resultados'}
                        styles={buildSelectStyles(isDark)} menuPortalTarget={document.body} />
                </Campo>
                {cliente && sedes.length > 1 && (
                    <Campo label="Dirección de instalación">
                        <select className={INPUT} value={sede?.id || ''} onChange={(e) => setSedeId(e.target.value)}>
                            {sedes.map(s => <option key={s.id} value={s.id}>{s.nombreSede ? `${s.nombreSede} · ` : ''}{s.direccion}</option>)}
                        </select>
                    </Campo>
                )}
                {!cliente && (
                    <Campo label="Condición frente al IVA">
                        <select className={INPUT} value={f.condicionIva} onChange={set('condicionIva')}>
                            {CONDICIONES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                        </select>
                    </Campo>
                )}

                <div className="grid grid-cols-3 gap-2">
                    <Campo label="Equipos"><input className={INPUT} inputMode="numeric" value={f.cantidad} onChange={set('cantidad')} /></Campo>
                    <Campo label="Tipo de equipo" className="col-span-2"><input className={INPUT} value={f.equipo} onChange={set('equipo')} /></Campo>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Campo label="Abono por equipo"><input className={INPUT} inputMode="numeric" value={f.abonoNeto} onChange={set('abonoNeto')} /></Campo>
                    <Campo label="Instalación c/u"><input className={INPUT} inputMode="numeric" value={f.instalacionNeto} onChange={set('instalacionNeto')} /></Campo>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <Campo label="Bonificadas"><input className={INPUT} inputMode="numeric" value={f.bonificadas} onChange={set('bonificadas')} /></Campo>
                    <Campo label="Contrato (meses)"><input className={INPUT} inputMode="numeric" value={f.contratoMeses} onChange={set('contratoMeses')} /></Campo>
                    <Campo label="Ajuste IPC (meses)"><input className={INPUT} inputMode="numeric" value={f.ajusteMeses} onChange={set('ajusteMeses')} /></Campo>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Campo label="Paga hasta el día"><input className={INPUT} inputMode="numeric" value={f.diaPagoHasta} onChange={set('diaPagoHasta')} /></Campo>
                    <Campo label="Validez (días)"><input className={INPUT} inputMode="numeric" value={f.validezDias} onChange={set('validezDias')} /></Campo>
                </div>
                {tipoDoc === 'completa' && (
                    <Campo label="Observaciones (opcional)">
                        <textarea className={`${INPUT} h-20 py-2.5 resize-none`} value={f.observaciones} onChange={set('observaciones')} />
                    </Campo>
                )}
                {tipoDoc === 'contrato' && (
                    <CamposContrato locador={locador} setLocador={setLocador} representante={representante} setRepresentante={setRepresentante}
                        preaviso={preaviso} setPreaviso={setPreaviso} INPUT={INPUT} Campo={Campo} />
                )}

                <div className="rounded-xl bg-chip p-3 space-y-1">
                    <div className="flex justify-between text-sm"><span className="text-muted font-bold">Abono mensual con IVA</span><span className="text-ink font-black">{pesos(resumen.mensual)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted font-bold">Pago inicial con IVA</span><span className="text-ink font-black">{pesos(resumen.inicial)}</span></div>
                </div>
            </div>
            {acta && <ModalActaEntrega cantidad={n(f.cantidad) || 1} equipo={f.equipo} onCerrar={() => setActa(false)} />}
        </ModalShell>
    );
}
